"use client";

import { useState, useEffect, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import Image from 'next/image';
import { Upload, Loader2 } from 'lucide-react';
import { LibraryPreview } from './LibraryPreview';
import { uploadMediaFile, getUserLibrary, type MediaItem } from '@/lib/supabase-storage';

interface MediaLibraryDrawerProps {
  showExpandLink?: boolean;
  showDropzone?: boolean; // Add new prop
}

export default function MediaLibraryDrawer({ 
  showExpandLink = false,
  showDropzone = false // Set default to false
}: MediaLibraryDrawerProps) {
  const { data: session } = useSession();
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);
  const [libraryItems, setLibraryItems] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    async function loadLibrary() {
      if (!session?.user?.id) {
        setIsLoading(false);
        return;
      }
      
      try {
        const items = await getUserLibrary(session.user.id);
        setLibraryItems(items);
      } catch (err) {
        setError('Failed to load library');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadLibrary();
  }, [session?.user?.id]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!session?.user?.id) return;
    
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setIsUploading(true);
    setError(null);
    
    try {
      const uploadPromises = files.map(file => uploadMediaFile(file, session.user.id!));
      const results = await Promise.all(uploadPromises);
      
      const newItems = results.filter((item): item is MediaItem => item !== null);
      setLibraryItems(prev => [...newItems, ...prev]);
      
    } catch (err) {
      setError('Upload failed');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!session?.user?.id) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    setError(null);
    setIsDragging(false);
    
    try {
      const uploadPromises = files.map(file => uploadMediaFile(file, session.user.id!));
      const results = await Promise.all(uploadPromises);
      
      const newItems = results.filter((item): item is MediaItem => item !== null);
      setLibraryItems(prev => [...newItems, ...prev]);
    } catch (err) {
      setError('Upload failed');
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  }, [session?.user?.id]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Show auth message only when trying to interact
  const showAuthMessage = (!session && (showDropzone || showExpandLink));

  // Early return for auth message
  if (showAuthMessage) {
    return (
      <div className="p-4 text-center">
        <Link 
          href="/login"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Sign in to access your library
        </Link>
      </div>
    );
  }

  // Show loading only when authenticated and loading
  if (session && isLoading) {
    return (
      <div className="text-center py-4">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  // Update the main return statement with adjusted styles
  return (
    <div 
      className="
        space-y-4
        bg-transparent
      "
      onDrop={showDropzone ? handleDrop : undefined}
      onDragOver={showDropzone ? handleDragOver : undefined}
      onDragLeave={showDropzone ? handleDragLeave : undefined}
    >
      {showExpandLink && (
        <Link 
          href="/library"
          className="
            block w-full p-3 text-center
            text-blue-600 dark:text-blue-400
            hover:text-blue-500 dark:hover:text-blue-300
            rounded-lg
            transition-colors
          "
        >
          Expand Library
        </Link>
      )}

      {showDropzone && (
        <div 
          className={`
            border-2 border-dashed rounded-lg p-6 
            text-center transition-colors
            ${isDragging 
              ? 'border-blue-500/50' 
              : 'border-gray-300/20 dark:border-gray-600/20'
            }
          `}
        >
          <input
            type="file"
            id="file-upload"
            className="hidden"
            multiple
            accept="image/*,video/*"
            onChange={handleFileUpload}
            disabled={isUploading}
          />
          <label 
            htmlFor="file-upload"
            className={`cursor-pointer flex flex-col items-center space-y-2 
              ${isUploading ? 'pointer-events-none' : ''}`}
          >
            {isUploading ? (
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            ) : (
              <Upload className="h-8 w-8 text-gray-400" />
            )}
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {isUploading ? 'Uploading...' : 'Drop files here or click to upload'}
            </span>
          </label>
        </div>
      )}

      {error && (
        <div className="text-red-500 text-sm text-center p-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {libraryItems.map((item, index) => (
          <button
            key={item.id}
            onClick={() => setSelectedItemIndex(index)}
            className={cn(
              "relative aspect-square rounded-lg",
              "group",
              "bg-white/5 hover:bg-white/10",
              "border border-white/10",
              "transition-all duration-200",
              "overflow-hidden"
            )}
          >
            <Image
              src={item.url}
              alt={item.title}
              fill
              className="object-cover"
            />
          </button>
        ))}
      </div>

      {selectedItemIndex !== null && (
        <LibraryPreview
          items={libraryItems}
          initialIndex={selectedItemIndex}
          onClose={() => setSelectedItemIndex(null)}
        />
      )}
    </div>
  );
}